import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";

/**
 * FlowFinder analytics — fire-and-forget client.
 *
 *  - Sends a `pageview` event on every route change (debounced 600ms)
 *  - Sends a heartbeat every 60s while the page is VISIBLE
 *  - Exposes window.flowfinderTrack(event, props) so any component can emit
 *
 * Failures are silently swallowed — analytics must never break the app.
 */
const POST_OPTS = { withCredentials: true };

function send(event, props = {}, page) {
  const path = page || (typeof window !== "undefined" ? window.location.pathname : "");
  const referrer = typeof document !== "undefined" ? document.referrer : "";
  return api
    .post(
      "/api/analytics/track",
      { event, page: path, referrer, props },
      POST_OPTS,
    )
    .catch(() => {});
}

function heartbeat() {
  return api.post("/api/analytics/heartbeat", {}, POST_OPTS).catch(() => {});
}

if (typeof window !== "undefined") {
  // Global shorthand: window.flowfinderTrack("scan_run", { filters_count: 3 })
  window.flowfinderTrack = send;
}

export default function useAnalytics() {
  const location = useLocation();
  const lastSent = useRef("");

  // Pageviews on route change. Fire immediately on first mount so anonymous
  // landing-page visitors are always counted. Debounce only subsequent route
  // changes (handles React StrictMode double-renders).
  useEffect(() => {
    const path = location.pathname + (location.search || "");
    if (lastSent.current === path) return;
    const firstHit = lastSent.current === "";
    lastSent.current = path;
    if (firstHit) {
      send("pageview", {}, path);
      return;
    }
    const t = setTimeout(() => send("pageview", {}, path), 250);
    return () => clearTimeout(t);
  }, [location.pathname, location.search]);

  // Heartbeats while visible
  useEffect(() => {
    let id = null;
    const tick = () => {
      if (typeof document === "undefined" || document.visibilityState === "visible") {
        heartbeat();
      }
    };
    tick();
    id = setInterval(tick, 60 * 1000);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);
}

export { send as track };
